import React, { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log errors to console for debugging
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
}

function ErrorFallback({ error }: ErrorFallbackProps) {
  return (
    <div className="min-h-screen bg-background text-white flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* StarTime branding */}
        <div className="mb-8">
          <h1 className="text-6xl font-bold text-accent mb-4">⚠️</h1>
          <h2 className="text-4xl font-bold mb-2">Oops! Something went wrong</h2>
          <p className="text-white/70 text-lg">
            We encountered an unexpected error. Don't worry, your quest continues!
          </p>
        </div>

        {/* Error details (only in development) */}
        {import.meta.env.DEV && error && (
          <div className="mb-8 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-left">
            <p className="font-mono text-sm text-red-400 break-all">
              {error.message}
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/"
            className="px-6 py-3 bg-accent text-background font-semibold rounded-lg hover:bg-accent/90 transition-colors"
          >
            Return to Home
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
